import { Alert } from 'components'
import { useBoolean } from 'hooks'
import { useDelayedAction } from 'hooks/useDelayedAction'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { Box } from 'theme-ui'
import { copyToClipboard } from '../utils'

type ContextType = {
  copy: (text: string, name?: string) => void
}
const CopyContext = createContext({} as ContextType)

export function CopyProvider({ children }: { children: ReactNode }) {
  const [isOpen, open, close] = useBoolean(false)
  const delayed = useDelayedAction()
  //close and then open so the new alert persists
  const reopen = useCallback(() => {
    close()
    delayed(open)
  }, [open, close, delayed])
  const [text, setText] = useState<string>('')
  const copyContext = useMemo(
    () => ({
      copy: (text: string, name: string = 'Text') => {
        if (text) {
          copyToClipboard(text)
          setText(name)
          reopen()
        } else {
          setText('Failed: nothing')
          reopen()
        }
      },
    }),
    [reopen, setText]
  )

  return (
    <CopyContext.Provider value={copyContext}>
      {children}
      {isOpen ? (
        <Box sx={{ position: 'relative', zIndex: 999 }}>
          <Alert variant="info" onClose={close} persistence={3000} open>
            <Box>{text} copied!</Box>
          </Alert>
        </Box>
      ) : null}
    </CopyContext.Provider>
  )
}

export const useCopyText = () => {
  return useContext(CopyContext)
}
